import type { components } from '@nrx/betty-contracts/codegen/ts/betty-integrations';
import { useState, type JSX, type ReactNode } from 'react';
import { Link } from 'react-router';

import { Eyebrow, IconBadge, StatusScreen } from '../components/ui';

/**
 * WelcomePage — entry route `/welcome`.
 * Statically imported (not lazy) to keep this as the initial chunk.
 * Bundle budget: this route's initial payload must be ≤ 150 KB Brotli compressed.
 *
 * Design + copy ported from the ACCESS Member Onboarding Survey prototype.
 * Responsive: mobile follows src/landing.jsx (phone layout); ≥lg follows
 * "ACCESS Member Landing (Desktop).html" (1120px wrap, hero 2-col grid,
 * 4-col features, forest stat card, 5-col steps, conditions band, footer
 * columns).
 *
 * Story 2.2: Handles two currentStep values from token activation response:
 * - 'ELIGIBILITY_PENDING': routes to /self-attestation (CMS API unavailable)
 * - 'QUALIFYING_CONDITION_PENDING': shows inline holding message (no redirect)
 *
 * EnrollmentCurrentStep is imported from @nrx/betty-contracts (generated from
 * the OpenAPI spec) — do NOT redeclare it as a local string-literal union.
 */

// Contract-first: use generated type, never hardcode string literals (Dev Notes rule)
export type EnrollmentCurrentStep = components['schemas']['EnrollmentCurrentStep'];

interface WelcomePageProps {
  /**
   * Current enrollment step from token activation response.
   * Undefined = normal flow (proceed to /consent).
   * `| undefined` suffix required by exactOptionalPropertyTypes: true.
   */
  currentStep?: EnrollmentCurrentStep | undefined;
  /** ULID of the enrollment session (needed for self-attestation URL).
   * `| undefined` suffix required by exactOptionalPropertyTypes: true.
   */
  enrollmentSessionId?: string | undefined;
}

const FEATURES: { color: string; bg: string; label: string; d: string; icon: JSX.Element }[] = [
  {
    color: '#EBA763',
    bg: '#FBEFDC',
    label: 'Nutrition education',
    d: 'Short, friendly lessons about eating for your health — in English or Spanish.',
    icon: <path d="M4 19V5a2 2 0 0 1 2-2h10l4 4v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2Z M8 9h6M8 13h8" />,
  },
  {
    color: '#789C36',
    bg: '#F2F7E8',
    label: 'Health tips',
    d: 'Bite-sized, personalized tips that fit your conditions and your daily routine.',
    icon: (
      <path d="M12 2a7 7 0 0 0-7 7c0 5-2 6-2 6h18s-2-1-2-6a7 7 0 0 0-7-7Z M10 21a2 2 0 0 0 4 0" />
    ),
  },
  {
    color: '#4095A5',
    bg: '#E8F4F7',
    label: 'Group consults',
    d: 'Live virtual sessions with a registered dietitian and others on the same journey.',
    icon: (
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2 M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z M23 21v-2a4 4 0 0 0-3-3.87 M16 3.13a4 4 0 0 1 0 7.75" />
    ),
  },
  {
    color: '#D86053',
    bg: '#FCE4E2',
    label: 'Recipe & shopping guides',
    d: 'Easy recipes and grocery lists built around the foods that help you most.',
    icon: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h7 M16 3l5 5M18 2l3 3" />,
  },
];

const STEPS: {
  n: string;
  color: string;
  darkText?: boolean;
  title: string;
  d: string;
  done?: boolean;
}[] = [
  {
    n: '✓',
    color: '#99C256',
    darkText: true,
    title: 'Your provider referred you',
    d: "Your care team referred you to NourishedRx — that part's already done.",
    done: true,
  },
  {
    n: '1',
    color: '#EBA763',
    darkText: true,
    title: 'Confirm your details & consent',
    d: 'Check your information and agree to participate in the program.',
  },
  {
    n: '2',
    color: '#52D9CB',
    darkText: true,
    title: 'Complete a short onboarding survey',
    d: 'About 5 minutes — your health, preferences, and how to reach you.',
  },
  {
    n: '3',
    color: '#4095A5',
    title: 'We tailor your support',
    d: 'We match you to the right level of guidance based on your answers.',
  },
  {
    n: '4',
    color: '#EB534B',
    title: 'Start with the NourishedRx app',
    d: 'Download the app and begin your nutrition journey.',
  },
];

const STATS: [string, string][] = [
  ['$0', 'Out-of-pocket cost for eligible members'],
  ['12mo', 'Of personalized nutrition support'],
  ['100%', 'Digital — from the comfort of home'],
  ['24/7', 'On-demand nutrition education, tailored to you'],
];

const CONDITIONS: { label: string; icon: JSX.Element }[] = [
  { label: 'Hypertension', icon: <path d="M3 12h4l2 5 4-12 2 7h6" /> },
  {
    label: 'Prediabetes / Obesity',
    icon: (
      <>
        <circle cx="12" cy="12" r="8" />
        <path d="M12 8v8M8 12h8" />
      </>
    ),
  },
  { label: 'Diabetes', icon: <path d="M12 3c3 4 5 6.5 5 10a5 5 0 0 1-10 0c0-3.5 2-6 5-10Z" /> },
  {
    label: 'Chronic Kidney Disease',
    icon: <path d="M8 4c4 0 8 3 8 8s-4 8-8 8M8 4v16M8 9c3 0 5 1 5 3s-2 3-5 3" />,
  },
  {
    label: 'ASCVD',
    icon: <path d="M12 20s-7-4.5-7-10a4 4 0 0 1 7-2.5A4 4 0 0 1 19 10c0 5.5-7 10-7 10Z" />,
  },
];

const FAQS: [string, string][] = [
  [
    'Is there any cost to me?',
    'No. For eligible Medicare members, NourishedRx is a covered benefit through the ACCESS program — no copays and no fees.',
  ],
  [
    'What will I need?',
    'A smartphone or device with internet access, and about 5 minutes to complete the onboarding survey. We’ll guide you through the rest.',
  ],
  [
    'How long does the program last?',
    'It’s a 12-month digital nutrition program. You can take part from home, on your own schedule, with support whenever you need it.',
  ],
  [
    'Is my information private?',
    'Yes. Your information is protected under HIPAA. We only share a short progress summary with your provider so they can support your care.',
  ],
  [
    'Do I have to be referred by a provider?',
    'Yes — ACCESS enrollment requires a provider referral. Since you’ve received this page, your provider has already referred you, so you’re ready to begin.',
  ],
];

function CtaButton({ to, className = '' }: { to: string; className?: string }): JSX.Element {
  return (
    <Link
      to={to}
      className={`inline-flex h-[54px] items-center justify-center gap-2 rounded-full bg-action-primary px-8 text-base font-extrabold text-white shadow-cta transition-[transform,box-shadow,background-color] hover:-translate-y-px hover:bg-action-primary-hover ${className}`}
    >
      Begin onboarding
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M5 12h14m-6-6l6 6-6 6"
          stroke="#fff"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Link>
  );
}

/** Constrains content to the desktop prototype's 1120px wrap. */
function Wrap({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div className={`mx-auto w-full max-w-[1120px] px-5 lg:px-8 ${className}`}>{children}</div>
  );
}

export function WelcomePage({
  currentStep,
  enrollmentSessionId,
}: WelcomePageProps = {}): JSX.Element {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  // QUALIFYING_CONDITION_PENDING → inline holding page (no redirect)
  if (currentStep === 'QUALIFYING_CONDITION_PENDING') {
    return (
      <StatusScreen
        icon={
          <IconBadge tone="honey">
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
              <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </IconBadge>
        }
        title="Your enrollment is on hold"
      >
        <p className="max-w-md">
          Your enrollment is on hold while we verify your qualifying condition. Your care team will
          be in touch.
        </p>
        <p className="max-w-md text-xs">
          No further action is needed right now. We&apos;ll contact you by email and text with next
          steps.
        </p>
      </StatusScreen>
    );
  }

  // ELIGIBILITY_PENDING → route to /self-attestation
  if (currentStep === 'ELIGIBILITY_PENDING') {
    const selfAttestationHref = enrollmentSessionId
      ? `/self-attestation?sessionId=${encodeURIComponent(enrollmentSessionId)}`
      : '/self-attestation';

    return (
      <StatusScreen
        icon={
          <IconBadge tone="coral">
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </IconBadge>
        }
        title="One more step"
      >
        <p className="max-w-md">
          Our eligibility system is temporarily unavailable. Please confirm a few details so we can
          hold your spot.
        </p>
        <div className="mt-4 w-full max-w-xs">
          <Link
            to={selfAttestationHref}
            className="inline-flex h-[52px] w-full items-center justify-center rounded-full bg-action-primary px-6 text-base font-bold text-white shadow-cta transition-colors hover:bg-action-primary-hover"
          >
            Continue
          </Link>
        </div>
      </StatusScreen>
    );
  }

  // Default: normal enrollment flow → /consent (with sessionId if available)
  const consentHref = enrollmentSessionId
    ? `/consent?sessionId=${encodeURIComponent(enrollmentSessionId)}`
    : '/consent';

  return (
    <main className="min-h-dvh w-full bg-white">
      {/* Top bar */}
      <header className="sticky top-0 z-50 border-b border-line bg-white/92 backdrop-blur-md">
        <Wrap className="flex items-center justify-between py-3 lg:py-4">
          <img
            src="/assets/logo-red.png"
            alt="NourishedRx"
            className="block h-[22px] lg:h-[30px]"
          />
          <div className="flex items-center gap-5">
            <span className="rounded-full bg-cream px-2.5 py-1 text-[10.5px] font-extrabold tracking-[0.6px] uppercase text-steel lg:px-3.5 lg:py-1.5 lg:text-xs">
              Medicare ACCESS
            </span>
            <Link
              to={consentHref}
              className="hidden h-12 items-center rounded-full bg-action-primary px-6 text-[15px] font-extrabold text-white shadow-cta transition-colors hover:bg-action-primary-hover lg:inline-flex"
            >
              Begin onboarding
            </Link>
          </div>
        </Wrap>
      </header>

      {/* HERO */}
      <section className="bg-cream pt-6 pb-7 lg:pt-[72px] lg:pb-20">
        <Wrap>
          <div className="grid items-center gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-14">
            <div>
              <Eyebrow>Digital nutrition care · Covered by Medicare</Eyebrow>
              <h1 className="mt-3 text-[28px] leading-[1.12] font-extrabold tracking-[-0.5px] text-forest lg:mt-4 lg:text-[clamp(2.3rem,4.4vw,3.4rem)] lg:leading-[1.08] lg:tracking-[-0.02em]">
                Welcome to NourishedRx — your provider referred you to{' '}
                <span className="text-coral">ACCESS</span>.
              </h1>
              <p className="mt-3.5 max-w-[42ch] text-[15px] leading-relaxed text-steel lg:mt-5 lg:text-[1.22rem] lg:leading-normal">
                Your care team referred you to a fully digital nutrition program — personalized
                guidance, recipes, and group support to help you manage your health,{' '}
                <strong className="text-coral-dark">at no cost to you.</strong>
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-3.5 lg:mt-7">
                <CtaButton
                  to={consentHref}
                  className="w-full lg:h-[58px] lg:w-auto lg:px-8 lg:text-[1.08rem]"
                />
                <a
                  href="#how"
                  className="hidden h-[58px] items-center rounded-full border-[1.5px] border-line px-8 text-[1.08rem] font-extrabold text-forest transition-colors hover:bg-forest-5 lg:inline-flex"
                >
                  How it works
                </a>
              </div>
              <p className="mt-2.5 text-center text-[12.5px] text-steel lg:mt-4 lg:text-left lg:text-[0.95rem]">
                Takes about 5 minutes · No payment needed
              </p>
            </div>

            {/* Eligibility badge — floats over hero panel on desktop */}
            <div className="lg:relative">
              <div className="hidden aspect-[4/3.4] w-full rounded-[22px] bg-gradient-to-br from-forest via-forest-90 to-[#0a4a3e] shadow-pop lg:block">
                <div className="flex h-full flex-col justify-end p-8">
                  <svg width="44" height="44" viewBox="0 0 40 40" fill="none" aria-hidden="true">
                    <g stroke="#52D9CB" strokeWidth="2" strokeLinejoin="round" fill="none">
                      <path d="M20 6c4 4 4 10 0 14-4-4-4-10 0-14z" />
                      <path d="M6 20c4-4 10-4 14 0-4 4-10 4-14 0z" />
                      <path d="M20 34c-4-4-4-10 0-14 4 4 4 10 0 14z" />
                      <path d="M34 20c-4 4-10 4-14 0 4-4 10-4 14 0z" />
                    </g>
                  </svg>
                  <p className="mt-4 text-xl leading-snug font-extrabold text-white">
                    Food is health.
                    <br />
                    And you&apos;re invited.
                  </p>
                </div>
              </div>
              <div className="mt-5 flex items-center gap-3 rounded-[14px] bg-white p-3.5 shadow-pop lg:absolute lg:bottom-6 lg:-left-2.5 lg:mt-0 lg:px-4.5">
                <span className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-lg bg-coral-soft lg:h-10 lg:w-10 lg:rounded-[11px]">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M20 6 9 17l-5-5" stroke="#D86053" strokeWidth="2.4" />
                  </svg>
                </span>
                <span className="text-xs leading-tight font-extrabold text-forest lg:text-[0.98rem]">
                  You&apos;re eligible
                  <span className="block text-[11px] font-semibold text-steel lg:text-[0.82rem]">
                    Verified through Medicare
                  </span>
                </span>
              </div>
            </div>
          </div>
        </Wrap>
      </section>

      {/* WHAT YOU GET */}
      <section id="what" className="pt-7 pb-2 lg:py-[86px]">
        <Wrap>
          <div className="lg:text-center">
            <Eyebrow className="text-brand-blue">What you get</Eyebrow>
            <h2 className="mt-2.5 text-[22px] leading-tight font-extrabold tracking-[-0.3px] text-forest lg:mt-3 lg:text-[clamp(1.8rem,3vw,2.5rem)]">
              Support built around your goals
            </h2>
            <p className="mt-2 text-sm leading-normal text-steel lg:mx-auto lg:mt-3.5 lg:max-w-[56ch] lg:text-[1.1rem] lg:leading-relaxed">
              Everything&apos;s digital — use it from home, on your own schedule, with help whenever
              you need it.
            </p>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:mt-10 lg:grid-cols-4 lg:gap-5">
            {FEATURES.map((f) => (
              <div
                key={f.label}
                className="flex items-start gap-3.5 rounded-2xl border border-line bg-white p-4 shadow-card lg:flex-col lg:rounded-[18px] lg:p-6"
              >
                <span
                  className="grid h-11 w-11 shrink-0 place-items-center rounded-xl lg:h-[52px] lg:w-[52px] lg:rounded-[14px]"
                  style={{ background: f.bg }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={f.color}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    {f.icon}
                  </svg>
                </span>
                <div className="flex-1 lg:mt-1">
                  <div className="text-[15px] font-extrabold text-forest lg:text-[1.08rem]">
                    {f.label}
                  </div>
                  <div className="mt-1 text-[13px] leading-normal text-steel lg:mt-1.5 lg:text-[0.96rem]">
                    {f.d}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Wrap>
      </section>

      {/* ABOUT ACCESS — forest band */}
      <section id="about" className="py-6 lg:bg-forest lg:py-[86px]">
        <Wrap>
          <div className="rounded-[20px] bg-forest px-5 py-6 text-white lg:grid lg:grid-cols-2 lg:items-center lg:gap-14 lg:rounded-none lg:bg-transparent lg:p-0">
            <div>
              <Eyebrow className="text-honey">About the program</Eyebrow>
              <h3 className="mt-2.5 text-[19px] leading-snug font-extrabold lg:mt-3 lg:text-[clamp(1.8rem,3vw,2.5rem)] lg:leading-[1.18]">
                A covered benefit through the <span className="lg:text-honey">ACCESS</span> Model
              </h3>
              <p className="mt-2.5 text-[13.5px] leading-relaxed text-[#CFE6DF] lg:mt-4 lg:text-[1.08rem] lg:leading-relaxed">
                ACCESS — Advancing Chronic Care with Effective, Scalable Solutions — is a new model
                from the CMS Innovation Center. NourishedRx delivers the nutrition side of it:
                digital guidance to help you manage your health through what you eat.
              </p>
              <div className="mt-4 flex flex-wrap gap-2 lg:mt-5.5">
                {['No cost to you', '100% digital', '12-month program'].map((t) => (
                  <span
                    key={t}
                    className="rounded-full bg-white/12 px-3 py-1.5 text-[11.5px] font-bold text-white lg:px-4 lg:py-2 lg:text-sm"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
            {/* Stat card — desktop prototype */}
            <div className="mt-6 rounded-2xl border border-white/14 bg-white/6 p-6 lg:mt-0 lg:rounded-[24px] lg:p-8.5">
              <div className="grid grid-cols-2 gap-6">
                {STATS.map(([big, label]) => (
                  <div key={big}>
                    <div className="text-3xl leading-none font-extrabold text-teal lg:text-[2.4rem]">
                      {big}
                    </div>
                    <div className="mt-2 text-[13px] text-[#CFE6DF] lg:text-[0.95rem]">{label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Wrap>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="pt-2 pb-2 lg:py-[86px]">
        <Wrap>
          <div className="lg:text-center">
            <Eyebrow className="text-lime">How it works</Eyebrow>
            <h2 className="mt-2.5 mb-4 text-[22px] leading-tight font-extrabold tracking-[-0.3px] text-forest lg:mt-3 lg:mb-0 lg:text-[clamp(1.8rem,3vw,2.5rem)]">
              From referral to your first lesson
            </h2>
          </div>

          {/* Mobile: vertical timeline */}
          <div className="lg:hidden">
            {STEPS.map((s, i) => (
              <div key={s.title} className={`flex gap-3.5 ${i === STEPS.length - 1 ? '' : 'pb-4'}`}>
                <div className="flex flex-col items-center">
                  <span
                    className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] text-[15px] font-black"
                    style={{ background: s.color, color: s.darkText ? '#01261F' : '#fff' }}
                  >
                    {s.n}
                  </span>
                  {i !== STEPS.length - 1 && <span className="mt-1 w-0.5 flex-1 bg-line" />}
                </div>
                <div className="flex-1 pt-1">
                  <div className="text-[15px] font-extrabold text-forest">
                    {s.title}
                    {s.done && (
                      <span className="ml-2 rounded-full bg-lime-soft px-2 py-0.5 text-[10.5px] font-extrabold tracking-[0.5px] uppercase text-lime">
                        Done
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-[13px] leading-normal text-steel">{s.d}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: 5-column grid */}
          <div className="hidden lg:mt-11 lg:grid lg:grid-cols-5 lg:gap-5">
            {STEPS.map((s) => (
              <div key={s.title}>
                <span
                  className="grid h-12 w-12 place-items-center rounded-[13px] text-xl font-black"
                  style={{ background: s.color, color: s.darkText ? '#01261F' : '#fff' }}
                >
                  {s.n}
                </span>
                <h4 className="mt-4 flex flex-wrap items-center gap-2 text-[1.08rem] font-extrabold text-forest">
                  {s.title}
                  {s.done && (
                    <span className="rounded-full bg-lime-soft px-2 py-0.5 text-[0.68rem] font-extrabold tracking-[0.06em] uppercase text-lime">
                      Done
                    </span>
                  )}
                </h4>
                <p className="mt-1.5 text-[0.96rem] leading-normal text-steel">{s.d}</p>
              </div>
            ))}
          </div>
        </Wrap>
      </section>

      {/* CONDITIONS — desktop prototype band */}
      <section id="conditions" className="hidden bg-cream lg:block lg:py-[86px]">
        <Wrap className="text-center">
          <Eyebrow>Who it helps</Eyebrow>
          <h2 className="mt-3 text-[clamp(1.8rem,3vw,2.5rem)] font-extrabold text-forest">
            Built for cardiometabolic health
          </h2>
          <p className="mx-auto mt-3.5 max-w-[60ch] text-[1.08rem] leading-relaxed text-steel">
            NourishedRx supports the conditions tracked by the ACCESS program — with food and
            guidance tailored to your needs.
          </p>
          <div className="mt-9 grid grid-cols-5 gap-4.5">
            {CONDITIONS.map((c) => (
              <div
                key={c.label}
                className="rounded-2xl border-t-4 border-coral bg-white px-4.5 py-7 shadow-card"
              >
                <div className="mx-auto mb-3.5 grid h-[52px] w-[52px] place-items-center rounded-full bg-coral-soft">
                  <svg
                    width="27"
                    height="27"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#D86053"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    {c.icon}
                  </svg>
                </div>
                <strong className="block leading-tight text-forest">{c.label}</strong>
              </div>
            ))}
          </div>
        </Wrap>
      </section>

      {/* FAQ */}
      <section id="faqs" className="pt-6 pb-2 lg:py-[86px]">
        <div className="mx-auto w-full max-w-[1120px] px-5 lg:max-w-[760px] lg:px-8">
          <Eyebrow className="text-coral-dark lg:hidden">Good to know</Eyebrow>
          <h2 className="mt-2.5 mb-3.5 text-[22px] leading-tight font-extrabold tracking-[-0.3px] text-forest lg:mt-0 lg:mb-9 lg:text-center lg:text-[clamp(1.8rem,3vw,2.5rem)]">
            Questions members ask
          </h2>
          <div>
            {FAQS.map(([q, a], i) => {
              const open = openFaq === i;
              return (
                <div key={q} className="border-b border-line">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(open ? null : i)}
                    aria-expanded={open}
                    className="flex w-full cursor-pointer items-center gap-3 px-0.5 py-4 text-left text-[15px] font-extrabold text-forest lg:gap-4 lg:py-6 lg:text-[1.16rem]"
                  >
                    <span
                      aria-hidden="true"
                      className={`shrink-0 text-[22px] leading-none text-coral transition-transform duration-200 lg:text-[1.7rem] ${
                        open ? 'rotate-45' : ''
                      }`}
                    >
                      +
                    </span>
                    <span className="flex-1 leading-snug">{q}</span>
                  </button>
                  <div
                    className={`overflow-hidden transition-[max-height] duration-300 ease-in-out ${
                      open ? 'max-h-60' : 'max-h-0'
                    }`}
                  >
                    <p className="px-0.5 pb-4 pl-[34px] text-[13.5px] leading-relaxed text-steel lg:pb-6 lg:pl-[38px] lg:text-[1.04rem]">
                      {a}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA BAND */}
      <section className="px-5 pt-5 pb-7 lg:bg-forest lg:p-0 lg:py-[86px]">
        <Wrap className="max-lg:px-0">
          <div className="rounded-[20px] bg-forest px-5 py-6 text-center lg:grid lg:grid-cols-[1fr_auto] lg:items-center lg:gap-9 lg:rounded-none lg:bg-transparent lg:p-0 lg:text-left">
            <div>
              <h2 className="text-[21px] leading-snug font-extrabold text-white lg:text-[clamp(1.8rem,3vw,2.5rem)]">
                Ready to get started?
              </h2>
              <p className="mx-auto mt-2.5 mb-4 max-w-sm text-[13.5px] leading-relaxed text-[#CFE6DF] lg:mx-0 lg:mb-0 lg:mt-3 lg:max-w-[54ch] lg:text-[1.08rem]">
                The onboarding survey takes about 5 minutes. Your answers help us personalize your
                support from day one.
              </p>
            </div>
            <CtaButton
              to={consentHref}
              className="w-full lg:h-[58px] lg:w-auto lg:text-[1.08rem]"
            />
          </div>
        </Wrap>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-line bg-white pt-8 pb-7 lg:pt-14 lg:pb-10">
        <Wrap>
          <div className="text-center lg:grid lg:grid-cols-[1.6fr_1fr_1fr] lg:gap-10 lg:text-left">
            <div>
              <img
                src="/assets/logo-red.png"
                alt="NourishedRx"
                className="mx-auto mb-4 block h-[24px] lg:mx-0 lg:h-[30px]"
              />
              <p className="mx-auto max-w-[38ch] text-[12.5px] leading-relaxed text-steel lg:mx-0 lg:text-[0.98rem]">
                A digital nutrition program for Medicare members, delivered through the ACCESS
                Model.
              </p>
            </div>
            <nav className="hidden lg:block" aria-label="The program">
              <h5 className="mb-3.5 text-[0.82rem] font-bold tracking-[0.08em] uppercase text-steel">
                The program
              </h5>
              <ul className="flex flex-col gap-2.5">
                {[
                  ['#what', 'What you get'],
                  ['#about', 'About ACCESS'],
                  ['#how', 'How it works'],
                  ['#faqs', 'FAQs'],
                ].map(([href, label]) => (
                  <li key={href}>
                    <a
                      href={href}
                      className="text-[0.98rem] font-semibold text-forest transition-colors hover:text-coral"
                    >
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
            <div className="hidden lg:block">
              <h5 className="mb-3.5 text-[0.82rem] font-bold tracking-[0.08em] uppercase text-steel">
                Get started
              </h5>
              <ul className="flex flex-col gap-2.5">
                <li>
                  <Link
                    to={consentHref}
                    className="text-[0.98rem] font-semibold text-forest transition-colors hover:text-coral"
                  >
                    Begin onboarding
                  </Link>
                </li>
                <li>
                  <span className="text-[0.98rem] font-semibold text-forest">
                    Contact us · 844-656-8450
                  </span>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-6 border-t border-line pt-5 text-center text-[11.5px] leading-relaxed text-steel lg:mt-10 lg:flex lg:flex-wrap lg:items-center lg:gap-5 lg:text-left lg:text-[0.88rem]">
            <span>NourishedRx · An ACCESS Model participant</span>
            <span className="block lg:inline">
              A covered benefit administered through the Medicare ACCESS program.
            </span>
            <span className="mt-2 block lg:mt-0 lg:inline">
              Questions? Call <strong className="text-forest">844-656-8450</strong>
            </span>
          </div>
        </Wrap>
      </footer>
    </main>
  );
}
